import React, { createContext, useContext, useMemo, useState } from "react";
import useEvent from "react-use-event-hook";

import { uniqueId } from "@utils/text";

export type AlertType = {
  id: string;
  type: "info" | "warning" | "error";
  message: { title: React.ReactNode; description: React.ReactNode } | string;
  important?: boolean;
  read: boolean;
};

type AlertsContext = {
  alerts: AlertType[];
  addAlert: (notification: {
    id?: AlertType["id"];
    type: AlertType["type"];
    message: AlertType["message"];
    important?: AlertType["important"];
  }) => void;
  readAlert: (id: AlertType["id"]) => void;
  clearAlerts: () => void;
};

export const AlertsContext = createContext<AlertsContext>({
  alerts: [],
  addAlert: () => {
    // do nothing
  },
  readAlert: () => {
    // do nothing
  },
  clearAlerts: () => {
    // do nothing
  },
});

export const AlertContextProvider = ({ children }: React.PropsWithChildren) => {
  const [alerts, setAlerts] = useState<{ [key: string]: AlertType }>({});

  const handleAlert = useEvent(
    (
      type: "add" | "remove" | "change" | "clear",
      alert?: Partial<AlertType>,
    ) => {
      if (type === "add") {
        setAlerts((prev) => {
          const id = alert?.id || uniqueId("notification-");
          return {
            ...prev,
            [id]: {
              id,
              read: false,
              important: false,
              type: alert?.type || "info",
              message: alert?.message || "",
              ...alert,
            },
          };
        });
      } else if (type === "remove" && alert) {
        setAlerts((prev) => {
          const newAlerts = { ...prev };
          if (alert.id) {
            delete newAlerts[alert.id];
          }
          return newAlerts;
        });
      } else if (type === "change" && alert) {
        setAlerts((prev) => {
          if (!alert.id) {
            return prev;
          }
          return {
            ...prev,
            [alert.id]: { ...prev[alert.id], ...alert },
          };
        });
      } else if (type === "clear") {
        setAlerts({});
      }
    },
  );

  const state = useMemo<AlertsContext>(
    () => ({
      alerts: Object.values(alerts).sort((a, b) => {
        if (a.read === b.read) return 0;
        if (a.read && !b.read) return 1;
        return -1;
      }),
      addAlert(alert) {
        handleAlert("add", alert);
      },
      readAlert(id) {
        handleAlert("change", { id, read: true });
      },
      clearAlerts() {
        handleAlert("clear");
      },
    }),
    [alerts, handleAlert],
  );

  return (
    <AlertsContext.Provider value={state}>{children}</AlertsContext.Provider>
  );
};

export const useAlerts = () => {
  return useContext(AlertsContext);
};
